import { useState } from "react";
import type { Appointment, TechnicianAttachment } from "@myapp/contract";
import {
  useAddAppointmentTechnicianAttachments,
  useRemoveAppointmentTechnicianAttachment,
  useUpdateAppointmentTechnicianComment,
} from "../../../entities/appointment";
import { feedbackErrorMessage } from "./feedbackErrorMessage";
import type { TechnicianFeedbackController } from "./types";
import { useFeedbackActor } from "./useFeedbackActor";

/**
 * The worker appointment page's feedback controller. Takes `null` so the page can call it above
 * its early returns — hooks cannot be conditional, the appointment can still be loading.
 *
 * Every action swallows its own rejection: the card shows the message, and a failed upload must
 * not throw out of an `onChange` handler.
 */
export function useAppointmentTechnicianFeedback(
  appointment: Appointment | null,
): TechnicianFeedbackController | null {
  const { canRemove } = useFeedbackActor();
  const updateComment = useUpdateAppointmentTechnicianComment();
  const addAttachments = useAddAppointmentTechnicianAttachments();
  const removeAttachment = useRemoveAppointmentTechnicianAttachment();
  const [removingId, setRemovingId] = useState<number | null>(null);

  if (!appointment) return null;

  const { id, workOrderId } = appointment;

  return {
    comment: appointment.technicianComment ?? "",
    attachments: appointment.technicianAttachments ?? [],
    canRemove,

    saveComment: async (next) => {
      await updateComment
        .mutateAsync({ id, workOrderId, dto: { technicianComment: next.trim() || null } })
        .catch(() => undefined);
    },
    isSavingComment: updateComment.isPending,
    commentError: updateComment.error ? feedbackErrorMessage(updateComment.error) : null,

    addFiles: async (files) => {
      if (!files.length) return;
      await addAttachments.mutateAsync({ id, workOrderId, files }).catch(() => undefined);
    },
    isUploading: addAttachments.isPending,
    uploadError: addAttachments.error ? feedbackErrorMessage(addAttachments.error) : null,

    removeAttachment: async (item: TechnicianAttachment) => {
      setRemovingId(item.id);
      try {
        await removeAttachment.mutateAsync({ id, workOrderId, attachmentId: item.id });
      } catch {
        // surfaced through `removeError`
      } finally {
        setRemovingId(null);
      }
    },
    removingId,
    removeError: removeAttachment.error ? feedbackErrorMessage(removeAttachment.error) : null,
  };
}
